import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

// Vendors have code/name, not the picker's default code/description.
const VENDOR_OPTION = {
  formatOptionLabel: (o) => (o.name ? `${o.code} - ${o.name}` : o.code),
  renderOption: (o) => (
    <>
      <div className="searchable-code-select__option-code">{o.code}</div>
      <div className="searchable-code-select__option-name">{o.name}</div>
    </>
  ),
};

// NOTE: shape is inferred from CE's CAPIFilterSettings_Vendors (apifilters.h)
// — MAPRANGE(codes) + status flag + 3 type checkboxes. Not yet verified
// against a live GET /group/{uuid}/filters payload.
function blankRule() {
  return {
    filter_type: "include",
    vendors: {
      from: "",
      to: "",
      status: "active",
      supplier: true,
      subcontractor: true,
      other: true,
    },
  };
}

export default function VendorsFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            vendors: {
              from: r.vendors?.from ?? "",
              to: r.vendors?.to ?? "",
              status: r.vendors?.status ?? "active",
              supplier: r.vendors?.supplier ?? true,
              subcontractor: r.vendors?.subcontractor ?? true,
              other: r.vendors?.other ?? true,
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateRule(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, vendors: { ...r.vendors, [field]: value } } : r
      )
    );
  }

  function updateFilterType(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, filter_type: value } : r))
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      vendors: {
        from: r.vendors.from || null,
        to: r.vendors.to || null,
        status: r.vendors.status || null,
        supplier: r.vendors.supplier,
        subcontractor: r.vendors.subcontractor,
        other: r.vendors.other,
      },
    }));

    onSave({ resource: "ven", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Vendors Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => (
              <div key={idx} className="eqfm-row">
                <select
                  className="jfm-type-select"
                  value={rule.filter_type}
                  onChange={(e) => updateFilterType(idx, e.target.value)}
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>

                <div className="eqfm-fields">
                  <div className="eqfm-inputs-row">
                    <div className="efm-field">
                      <label>From Vendor</label>
                      <SearchableCodeSelect
                        value={rule.vendors.from}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getVendors}
                        {...VENDOR_OPTION}
                        emptyMessage="No vendors found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "from", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Vendor</label>
                      <SearchableCodeSelect
                        value={rule.vendors.to}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getVendors}
                        {...VENDOR_OPTION}
                        emptyMessage="No vendors found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "to", code)}
                      />
                    </div>
                    <div className="efm-field efm-field--status">
                      <label>Status</label>
                      <select
                        value={rule.vendors.status || "active"}
                        onChange={(e) =>
                          updateRule(idx, "status", e.target.value)
                        }
                      >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="eqfm-checkboxes-row">
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.vendors.supplier}
                        onChange={(e) =>
                          updateRule(idx, "supplier", e.target.checked)
                        }
                      />
                      Suppliers
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.vendors.subcontractor}
                        onChange={(e) =>
                          updateRule(idx, "subcontractor", e.target.checked)
                        }
                      />
                      Subcontractors
                    </label>
                    <label className="eqfm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={rule.vendors.other}
                        onChange={(e) =>
                          updateRule(idx, "other", e.target.checked)
                        }
                      />
                      Others
                    </label>
                  </div>
                </div>

                {!readOnly && (
                  <button
                    className="jfm-delete"
                    onClick={() => removeRule(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
